import {
  Entity,
  EntitySelector as BaseEntitySelector,
  Flex,
} from '@tabeo/scarf'
import { format } from '@tabeo/sharpei/utils/currency'
import useFetcher from 'hooks/useFetcher'
import moment from 'moment'
import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { useBills } from 'resources/Bills'
import { useCollectionAccounts } from 'resources/CollectionAccounts'
import { useLeads } from 'resources/Leads'
import { useMerchants } from 'resources/Merchants'
import { useTicket } from 'resources/Ticket'
import { useTickets } from 'resources/Tickets'
import { useTransactions } from 'resources/Transactions'

function EntitySelector({ ...rest }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { data } = useTicket()

  const fetchTransactions = useFetcher(useTransactions)
  const fetchLeads = useFetcher(useLeads)
  const fetchTickets = useFetcher(useTickets)
  const fetchMerchants = useFetcher(useMerchants)
  const fetchBills = useFetcher(useBills)
  const fetchConsumers = useFetcher(useCollectionAccounts)

  return (
    <BaseEntitySelector
      types={[
        {
          type: 'offers',
          label: 'Transaction',
          loader: fetchTransactions,
          placeholder: 'Transaction ID or name',
          link: ({ id }) =>
            navigate({
              pathname: `/transactions/${id}`,
              search: location.search,
              hash: location.hash,
            }),
          renderItem: (
            // eslint-disable-next-line react/prop-types
            { data: { id, title, price } },
            { ...rest }
          ) => (
            <Entity color="default.teal" {...rest}>
              <Entity.Id
                as={Link}
                to={{
                  ...location,
                  pathname: `/transactions/${id}`,
                }}
                onClick={e => e.stopPropagation()}
              >
                {id}
              </Entity.Id>
              <Entity.Content>
                <Entity.Text mr={2} flexShrink="0.001">
                  {title}
                </Entity.Text>
                <Flex flex="1" />
                <Entity.Text color="ink.2" flexShrink="1000">
                  {format(price)}
                </Entity.Text>
              </Entity.Content>
              <Entity.Tick />
            </Entity>
          ),
        },
        {
          type: 'leads',
          label: 'Lead',
          loader: fetchLeads,
          placeholder: 'Lead ID or name',
          link: ({ id }) =>
            navigate({
              pathname: `/leads/${id}`,
              search: location.search,
              hash: location.hash,
            }),
          renderItem: (
            // eslint-disable-next-line react/prop-types
            { data: { id, firstName, lastName } },
            { ...rest }
          ) => (
            <Entity color="default.teal" {...rest}>
              <Entity.Id
                as={Link}
                to={{
                  ...location,
                  pathname: `/leads/${id}`,
                }}
                onClick={e => e.stopPropagation()}
              >
                {id}
              </Entity.Id>
              <Entity.Content>
                <Entity.Text>
                  {firstName} {lastName}
                </Entity.Text>
              </Entity.Content>
              <Entity.Tick />
            </Entity>
          ),
        },
        {
          type: 'tickets',
          label: 'Ticket',
          loader: fetchTickets,
          placeholder: 'Ticket ID or name',
          disabledValues: [data?.id],
          link: ({ id }) =>
            navigate(
              {
                pathname: `/tickets`,
                search: `?ticketId=${id}`,
                hash: location.hash,
              },
              { replace: true }
            ),
          renderItem: (
            // eslint-disable-next-line react/prop-types
            { data: { id, title } },
            { ...rest }
          ) => (
            <Entity color="default.teal" {...rest}>
              <Entity.Id
                as={Link}
                to={{
                  ...location,
                  search: `?ticketId=${id}`,
                  pathname: `/tickets`,
                }}
                onClick={e => e.stopPropagation()}
                replace
              >
                {id}
              </Entity.Id>
              <Entity.Content>
                <Entity.Text>{title}</Entity.Text>
              </Entity.Content>
              <Entity.Tick />
            </Entity>
          ),
        },
        {
          type: 'merchants',
          label: 'Merchant',
          loader: fetchMerchants,
          placeholder: 'Merchant ID or name',
          link: ({ id }) =>
            navigate({
              pathname: `/merchants/${id}`,
              search: location.search,
              hash: location.hash,
            }),
          renderItem: (
            // eslint-disable-next-line react/prop-types
            { data: { id, tradingName } },
            { ...rest }
          ) => (
            <Entity color="default.teal" {...rest}>
              <Entity.Id
                as={Link}
                to={{
                  ...location,
                  pathname: `/merchants/${id}`,
                }}
                onClick={e => e.stopPropagation()}
              >
                {id}
              </Entity.Id>
              <Entity.Content>
                <Entity.Text>{tradingName}</Entity.Text>
              </Entity.Content>
              <Entity.Tick />
            </Entity>
          ),
        },
        {
          type: 'consumers',
          label: 'Consumer',
          loader: fetchConsumers,
          placeholder: 'Consumer ID or name',
          link: ({ id }) =>
            navigate({
              pathname: `/collection-accounts/${id}`,
              search: location.search,
              hash: location.hash,
            }),
          renderItem: (
            // eslint-disable-next-line react/prop-types
            { data: { id, firstName, lastName } },
            { ...rest }
          ) => (
            <Entity color="default.teal" {...rest}>
              <Entity.Id
                as={Link}
                to={{
                  ...location,
                  pathname: `/collection-accounts/${id}`,
                }}
                onClick={e => e.stopPropagation()}
              >
                {id}
              </Entity.Id>
              <Entity.Content>
                <Entity.Text>
                  {firstName} {lastName}
                </Entity.Text>
              </Entity.Content>
              <Entity.Tick />
            </Entity>
          ),
        },
        {
          type: 'bills',
          label: 'Bill',
          loader: fetchBills,
          placeholder: 'Bill ID or name',
          link: ({ id }) =>
            navigate({
              pathname: `/bills/${id}`,
              search: location.search,
              hash: location.hash,
            }),
          renderItem: (
            // eslint-disable-next-line react/prop-types
            { data: { id, title, scheduledAt } },
            { ...rest }
          ) => (
            <Entity color="default.teal" {...rest}>
              <Entity.Id
                as={Link}
                to={{
                  ...location,
                  pathname: `/bills/${id}`,
                }}
                onClick={e => e.stopPropagation()}
              >
                {id}
              </Entity.Id>
              <Entity.Content>
                <Entity.Text mr={2} flexShrink="0.001">
                  {title}
                </Entity.Text>
                <Flex flex="1" />
                <Entity.Text color="ink.2" flexShrink="1000">
                  {moment(scheduledAt).format('DD MMM YYYY')}
                </Entity.Text>
              </Entity.Content>
              <Entity.Tick />
            </Entity>
          ),
        },
      ]}
      {...rest}
    />
  )
}

export default EntitySelector
